import styles from './styles.module.scss';
import { useCountdown, useTranslation } from '../../../../hooks';
import { Counter } from '@starsoft/common/components/Counter';
import { TransactionResultExpiresAtProps } from './props';
import { useQueryClient } from '@tanstack/react-query';
import { DepositQueryKeys, WithdrawalsQueryKeys } from '@starsoft/common/api';
import { DepositStatusId } from '@starsoft/common/models/deposit/status/id';
import { WithdrawalStatusId } from '@starsoft/common/models/withdraw/status/id';

/**
 * TransactionResultExpiresAt Component
 * Renders a countdown timer showing when a transaction will expire.
 * The countdown is only shown for pending/created deposits and non-expired withdrawals.
 * When countdown completes, it invalidates related queries to refresh transaction data.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Withdraw | Deposit} props.transaction - The transaction object containing status and expiry details
 * @returns {JSX.Element | null} A container with expiry disclaimer text and countdown timer, or null if expired withdrawal
 *
 * @example
 * const transaction = {
 *   status: { id: DepositStatusId.Pending },
 *   expiresAt: "2024-01-01T00:00:00Z"
 * };
 *
 * <TransactionResultExpiresAt transaction={transaction} />
 */
export default function TransactionResultExpiresAt({
  transaction,
}: TransactionResultExpiresAtProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  // Setup countdown hook with auto-refresh on completion
  const countdown = useCountdown({
    disabled:
      !transaction?.expiresAt ||
      ![
        DepositStatusId.Pending?.toString(),
        DepositStatusId.Created?.toString(),
      ].includes(transaction?.status?.id?.toString() ?? ''),
    targetDate: transaction?.expiresAt?.toString() ?? new Date().toString(),
    onCountdownComplete: () => {
      // Invalidate queries to refresh transaction data
      queryClient.invalidateQueries({
        queryKey: [DepositQueryKeys.ById],
      });
      queryClient.invalidateQueries({
        queryKey: [DepositQueryKeys.Primary],
      });
      queryClient.invalidateQueries({
        queryKey: [WithdrawalsQueryKeys.ById],
      });
      queryClient.invalidateQueries({
        queryKey: [WithdrawalsQueryKeys.Primary],
      });
    },
  });

  // Don't show countdown for expired withdrawals
  if (
    transaction?.status?.id === WithdrawalStatusId.Expired &&
    transaction?.expiresAt
  ) {
    return null;
  }

  return (
    <div className={styles.container}>
      <span className={styles.container__label}>
        {t('expires_at_disclaimer')}
      </span>
      <Counter
        isContainerColor
        countdown={countdown}
        hideDays={countdown?.days == '00'}
        hideHours={countdown?.hours == '00'}
      />
    </div>
  );
}
